import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useUser } from "../pages/Home/UserContext";

import { getActeursByUser } from "../services/userServices";
import { getAllEventsByActorId } from "../services/acteureventServices";

const ConvocationContext = createContext(undefined);

const DEFAULT_POLL_INTERVAL = 60_000;

/**
 * Récupère l'utilisateur conservé dans le localStorage
 * lorsque le UserContext n'est pas encore initialisé.
 */
const getStoredUser = () => {
  if (typeof window === "undefined") return null;

  try {
    const storedUser = localStorage.getItem("userInfo");

    return storedUser ? JSON.parse(storedUser) : null;
  } catch (error) {
    console.error(
      "Impossible de lire userInfo depuis le localStorage.",
      error,
    );

    return null;
  }
};

/**
 * Uniformise le statut de réponse.
 */
const normalizeResponseStatus = (status) => {
  const normalizedStatus = String(status || "pending")
    .trim()
    .toLowerCase();

  if (normalizedStatus === "accepted") return "accepted";
  if (normalizedStatus === "rejected") return "rejected";

  return "pending";
};

/**
 * Récupère la table de liaison ActeursEvents,
 * quelle que soit la clé renvoyée par Sequelize.
 */
const getEventPivot = (event) => {
  return (
    event?.ActeursEvents ||
    event?.ActeurEvent ||
    event?.ActeursEvent ||
    {}
  );
};

/**
 * Transforme les événements d'un acteur en convocations.
 */
const normalizeConvocationsFromActor = (acteurData) => {
  if (!acteurData) return [];

  const events = Array.isArray(acteurData.Events)
    ? acteurData.Events
    : [];

  return events.map((event) => {
    const pivot = getEventPivot(event);

    return {
      id:
        pivot.id ??
        `${acteurData.id || "acteur"}-${event?.id || "event"}`,

      acteur_id: acteurData.id,
      event_id: event?.id,

      poste: pivot.poste ?? "",
      tapis: pivot.tapis ?? "",

      is_validate: pivot.is_validate ?? false,
      note: pivot.note ?? null,
      observations: pivot.observations ?? "",

      response_status: normalizeResponseStatus(
        pivot.response_status,
      ),

      response_reason: pivot.response_reason ?? "",
      responded_at: pivot.responded_at ?? null,

      need_transport_support:
        pivot.need_transport_support ?? null,

      need_accommodation_support:
        pivot.need_accommodation_support ?? null,

      support_request_comment:
        pivot.support_request_comment ?? "",

      support_requested_at:
        pivot.support_requested_at ?? null,

      transport_support_status:
        pivot.transport_support_status ?? null,

      accommodation_support_status:
        pivot.accommodation_support_status ?? null,

      Event: event,

      acteur: {
        id: acteurData.id,
        prenom: acteurData.prenom,
        nom: acteurData.nom,
        club_acteur: acteurData.club_acteur,
      },
    };
  });
};

/**
 * Détermine si la convocation concerne un événement
 * qui n'est pas encore terminé.
 */
const isUpcomingConvocation = (convocation) => {
  const event = convocation?.Event;

  if (!event) return true;

  /*
   * Lorsque la date de fin existe, elle est plus pertinente :
   * une compétition commencée mais non terminée reste actuelle.
   */
  const dateValue = event.end || event.start;

  if (!dateValue) return true;

  const eventDate = new Date(dateValue);

  if (Number.isNaN(eventDate.getTime())) return true;

  return eventDate.getTime() >= Date.now();
};

export function ConvocationProvider({
  children,
  pollIntervalMs = DEFAULT_POLL_INTERVAL,
}) {
  const userContext = useUser();

  const user = userContext?.user ?? getStoredUser();

  const userId = user?.userId || user?.id || null;

  const [convocations, setConvocations] = useState([]);
  const [loadingConvocations, setLoadingConvocations] =
    useState(false);
  const [convocationError, setConvocationError] = useState(null);
  const [lastConvocationRefresh, setLastConvocationRefresh] =
    useState(null);

  const requestIdRef = useRef(0);
  const mountedRef = useRef(false);

  /**
   * Recharge toutes les convocations des acteurs liés
   * à l'utilisateur connecté.
   */
  const refreshPendingConvocations = useCallback(
    async ({ silent = false } = {}) => {
      const currentRequestId = ++requestIdRef.current;

      if (!userId) {
        setConvocations([]);
        setConvocationError(null);
        setLoadingConvocations(false);
        setLastConvocationRefresh(null);

        return [];
      }

      if (!silent) {
        setLoadingConvocations(true);
      }

      setConvocationError(null);

      try {
        const acteursData = await getActeursByUser(userId);

        if (
          !Array.isArray(acteursData) ||
          acteursData.length === 0
        ) {
          if (
            mountedRef.current &&
            currentRequestId === requestIdRef.current
          ) {
            setConvocations([]);
            setLastConvocationRefresh(new Date());
          }

          return [];
        }

        /*
         * allSettled empêche l'échec du chargement complet
         * lorsqu'un seul profil acteur rencontre une erreur.
         */
        const acteursResults = await Promise.allSettled(
          acteursData.map((acteur) =>
            getAllEventsByActorId(acteur.id),
          ),
        );

        const acteursWithEvents = acteursResults
          .filter((result) => result.status === "fulfilled")
          .map((result) => result.value)
          .filter(Boolean);

        const rejectedResults = acteursResults.filter(
          (result) => result.status === "rejected",
        );

        if (rejectedResults.length > 0) {
          console.warn(
            `${rejectedResults.length} profil(s) acteur n'ont pas pu être chargés.`,
            rejectedResults,
          );
        }

        const nextConvocations = acteursWithEvents.flatMap(
          normalizeConvocationsFromActor,
        );

        if (
          mountedRef.current &&
          currentRequestId === requestIdRef.current
        ) {
          setConvocations(nextConvocations);
          setLastConvocationRefresh(new Date());
        }

        return nextConvocations;
      } catch (error) {
        console.error(
          "Erreur lors du chargement du compteur de convocations.",
          error,
        );

        if (
          mountedRef.current &&
          currentRequestId === requestIdRef.current
        ) {
          setConvocations([]);

          setConvocationError(
            error?.response?.data?.message ||
              "Impossible de charger les convocations.",
          );
        }

        return [];
      } finally {
        if (
          mountedRef.current &&
          currentRequestId === requestIdRef.current
        ) {
          setLoadingConvocations(false);
        }
      }
    },
    [userId],
  );

  /**
   * Permet à la page ConvocationsPage de transmettre directement
   * ses données au contexte, sans refaire une nouvelle requête.
   */
  const syncPendingConvocations = useCallback(
    (nextConvocations) => {
      if (!Array.isArray(nextConvocations)) {
        setConvocations([]);
        return;
      }

      const normalizedConvocations = nextConvocations.map(
        (convocation) => ({
          ...convocation,

          response_status: normalizeResponseStatus(
            convocation?.response_status,
          ),
        }),
      );

      setConvocations(normalizedConvocations);
      setConvocationError(null);
      setLastConvocationRefresh(new Date());
    },
    [],
  );

  /**
   * Met immédiatement à jour une convocation après une réponse.
   * Cela évite d'attendre la prochaine requête pour actualiser le badge.
   */
  const markConvocationAsAnswered = useCallback(
    ({
      convocationId,
      acteurId,
      eventId,
      responseStatus,
      respondedAt = new Date().toISOString(),
    }) => {
      const normalizedStatus =
        normalizeResponseStatus(responseStatus);

      setConvocations((previousConvocations) =>
        previousConvocations.map((convocation) => {
          const matchesById =
            convocationId != null &&
            String(convocation.id) === String(convocationId);

          const matchesByActorAndEvent =
            acteurId != null &&
            eventId != null &&
            String(convocation.acteur_id) === String(acteurId) &&
            String(convocation.event_id) === String(eventId);

          if (!matchesById && !matchesByActorAndEvent) {
            return convocation;
          }

          return {
            ...convocation,
            response_status: normalizedStatus,
            responded_at: respondedAt,
          };
        }),
      );
    },
    [],
  );

  /**
   * Toutes les convocations dont la réponse est en attente.
   */
  const pendingConvocations = useMemo(() => {
    return convocations.filter(
      (convocation) =>
        normalizeResponseStatus(
          convocation?.response_status,
        ) === "pending",
    );
  }, [convocations]);

  /**
   * Convocations en attente sur des événements à venir
   * ou encore en cours.
   */
  const pendingFutureConvocations = useMemo(() => {
    return pendingConvocations.filter(isUpcomingConvocation);
  }, [pendingConvocations]);

  const pendingConvocationCount = pendingConvocations.length;

  const pendingFutureConvocationCount =
    pendingFutureConvocations.length;

  /**
   * Premier chargement et changement d'utilisateur.
   */
  useEffect(() => {
    mountedRef.current = true;

    refreshPendingConvocations();

    return () => {
      mountedRef.current = false;
      requestIdRef.current += 1;
    };
  }, [refreshPendingConvocations]);

  /**
   * Rafraîchissement périodique du compteur.
   */
  useEffect(() => {
    if (
      !userId ||
      !pollIntervalMs ||
      pollIntervalMs < 10_000
    ) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      refreshPendingConvocations({
        silent: true,
      });
    }, pollIntervalMs);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [
    pollIntervalMs,
    refreshPendingConvocations,
    userId,
  ]);

  /**
   * Rafraîchissement lorsque l'utilisateur revient
   * sur l'application ou sur l'onglet.
   */
  useEffect(() => {
    if (!userId) return undefined;

    const handleWindowFocus = () => {
      refreshPendingConvocations({
        silent: true,
      });
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refreshPendingConvocations({
          silent: true,
        });
      }
    };

    window.addEventListener("focus", handleWindowFocus);
    document.addEventListener(
      "visibilitychange",
      handleVisibilityChange,
    );

    return () => {
      window.removeEventListener("focus", handleWindowFocus);

      document.removeEventListener(
        "visibilitychange",
        handleVisibilityChange,
      );
    };
  }, [refreshPendingConvocations, userId]);

  const contextValue = useMemo(
    () => ({
      convocations,

      pendingConvocations,
      pendingFutureConvocations,

      /*
       * Compteur de toutes les convocations en attente.
       */
      pendingConvocationCount,

      /*
       * Compteur recommandé pour la Topbar et la Navbar :
       * uniquement les événements à venir ou en cours.
       */
      pendingFutureConvocationCount,

      hasPendingConvocations:
        pendingFutureConvocationCount > 0,

      loadingConvocations,
      convocationError,
      lastConvocationRefresh,

      refreshPendingConvocations,
      syncPendingConvocations,
      markConvocationAsAnswered,
    }),
    [
      convocations,
      pendingConvocations,
      pendingFutureConvocations,
      pendingConvocationCount,
      pendingFutureConvocationCount,
      loadingConvocations,
      convocationError,
      lastConvocationRefresh,
      refreshPendingConvocations,
      syncPendingConvocations,
      markConvocationAsAnswered,
    ],
  );

  return (
    <ConvocationContext.Provider value={contextValue}>
      {children}
    </ConvocationContext.Provider>
  );
}

export function useConvocation() {
  const context = useContext(ConvocationContext);

  if (context === undefined) {
    throw new Error(
      "useConvocation doit être utilisé à l'intérieur de <ConvocationProvider>.",
    );
  }

  return context;
}

export default ConvocationContext;